import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useVolunteerSession } from "../lib/volunteerSession";
import { supabase } from "../lib/supabaseClient";
import { AccountIcon, ParishChip } from "./DashboardHeader";

// The volunteer portal's header — parish chip + account menu on the
// right, and the personal greeting ("Hi, Michael") in the top-left
// where a brand mark would otherwise sit. No logo: the parish chip
// already carries Our Lady of Grace's name + icon, so a second parish
// mark in the same row was redundant. (The admin DashboardHeader keeps
// its Sacristy wordmark — that's a different, product-branded surface.)
export default function VolunteerHeader({
  loginPath,
  greeting,
  scheduleUrl,
}: {
  loginPath: string;
  // The "Hi, {name}" title, rendered in the top-left slot.
  greeting?: ReactNode;
  // Link to the parish's master calendar (public schedule page). When
  // provided, a calendar icon appears in the header. Undefined while
  // the dashboard is still loading or before migration 0008.
  scheduleUrl?: string;
}) {
  const session = useVolunteerSession();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const signOut = async () => {
    setMenuOpen(false);
    if (supabase) await supabase.auth.signOut();
    navigate(loginPath);
  };

  return (
    <header className="dashboard-header dashboard-header--volunteer">
      <div className="dashboard-header__top">
        {greeting}
        <div className="dashboard-header__right" ref={wrapperRef}>
          <ParishChip />
          {scheduleUrl ? (
            <Link
              to={scheduleUrl}
              className="dashboard-header__cal"
              aria-label="Parish schedule"
              title="Parish schedule"
            >
              <CalendarIcon />
            </Link>
          ) : null}
          <button
            type="button"
            className="dashboard-header__account"
            aria-label="Account menu"
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            onClick={() => setMenuOpen((v) => !v)}
          >
            <AccountIcon />
          </button>
          {menuOpen ? (
            <div className="dashboard-header__menu" role="menu">
              {session.displayName ? (
                <p className="dashboard-header__menu-name">{session.displayName}</p>
              ) : null}
              <p className="dashboard-header__menu-email">{session.email}</p>
              <div className="dashboard-header__menu-divider" />
              <button
                type="button"
                className="dashboard-header__menu-item"
                onClick={signOut}
                role="menuitem"
              >
                Sign out
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}

// Simple calendar glyph — a bound page with a header bar and a couple
// of date marks. Sized/coloured by .dashboard-header__cal.
function CalendarIcon() {
  return (
    <svg viewBox="0 0 16 16" width="17" height="17" aria-hidden="true">
      <rect x="1.5" y="2.5" width="13" height="12" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <line x1="1.5" y1="6" x2="14.5" y2="6" stroke="currentColor" strokeWidth="1.4" />
      <line x1="5" y1="1.5" x2="5" y2="3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <line x1="11" y1="1.5" x2="11" y2="3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="5.2" cy="9" r="1" fill="currentColor" />
      <circle cx="8" cy="9" r="1" fill="currentColor" />
      <circle cx="10.8" cy="9" r="1" fill="currentColor" />
      <circle cx="5.2" cy="11.8" r="1" fill="currentColor" />
      <circle cx="8" cy="11.8" r="1" fill="currentColor" />
    </svg>
  );
}
